import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { getApiUrl } from '../config';

// Yardımcı fonksiyon: Rastgele paylaşım kodu oluşturma
const generateRandomCode = (prefix = 'REF', length = 8) => {
  const characters = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const timestamp = Date.now().toString(36);
  const randomPart = Array.from(
    window.crypto.getRandomValues(new Uint8Array(length))
  )
    .map(byte => characters[byte % characters.length])
    .join('');
  const code = `${prefix}-${timestamp.slice(-4)}${randomPart.slice(0, length - 4)}`;
  return code.toUpperCase();
};

// Yüzük ürünü kontrolü
const isRingProduct = (item) => {
  if (!item.kategori) return false;
  return item.kategori.includes("75") || item.kategori.includes("103") || item.kategori.includes("112");
};

export const cartApi = createApi({
  reducerPath: 'cartApi',
  baseQuery: fetchBaseQuery({
    baseUrl: getApiUrl(),
    prepareHeaders: (headers) => {
      const token = localStorage.getItem('token');
      const tkn = localStorage.getItem('tkn');
      
      if (token) {
        headers.set('Authorization', `${token}`);
      }
      if (tkn) {
        headers.set('Tkn', `${tkn}`);
      }
      headers.set('Content-Type', 'application/json');
      return headers;
    },
  }),
  tagTypes: ['Cart', 'SharedCart'],
  endpoints: (builder) => ({
    getCart: builder.query({
      query: () => '/cart',
      transformResponse: (response) => {
        return response.map(cart => {
          if (typeof cart.image === 'string') {
            try {
              const imageUrls = JSON.parse(cart.image.replace(/\\/g, ''));
              return { ...cart, image: imageUrls };
            } catch (error) {
              console.error('Image JSON parse edilemedi:', error);
              return cart;
            }
          }
          
          // Yüzük ürünü kontrolü ve ring_size bilgisi ekleme
          if (isRingProduct(cart)) {
            // Eğer ring_size yoksa varsayılan değer ata
            if (!cart.ring_size) {
              cart.ring_size = cart.size || 15;
            }
          }
          
          return cart;
        });
      },
      providesTags: ['Cart'],
    }),
    
    addToCart: builder.mutation({
      query: ({ urun_id, size }) => ({
        url: '/cart',
        method: 'POST',
        body: { urun_id, ...(size && { size }) },
      }),
      invalidatesTags: ['Cart'],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Facebook Pixel event
          fbq('track', 'AddToCart');
        } catch (err) {
          console.error('Sepete ekleme hatası:', err);
        }
      },
    }),

    deleteFromCart: builder.mutation({
      query: (id) => ({
        url: `/cart?id=${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Cart'],
    }),

    clearCart: builder.mutation({
      query: () => ({
        url: '/cart?clear_all=true',
        method: 'DELETE',
      }),
      invalidatesTags: ['Cart'],
    }),

    // Hizmet işlemleri
    addService: builder.mutation({
      query: ({ cart_item_id, title, description, price }) => ({
        url: `/cart/service?cart_item_id=${cart_item_id}&action=add`,
        method: 'POST',
        body: { cart_item_id, title, description, price },
      }),
      invalidatesTags: ['Cart'],
    }),

    getServices: builder.query({
      query: (cart_item_id) => `/cart/service?cart_item_id=${cart_item_id}`,
      providesTags: ['Cart'],
    }),

    deleteService: builder.mutation({
      query: (service_id) => ({
        url: `/cart/service?service_id=${service_id}&action=del`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Cart'],
    }),

    // Kupon bilgisi getirme
    // getCouponByRef: builder.query({
    //   query: (refCode) => `/cart/coupon?ref=${refCode}`,
    //   transformResponse: (response) => {
    //     if (!response || response.error) {
    //       return { yuzde: 0, kupon: 0, message: 'Kupon bulunamadı', success: false };
    //     }
    //     return {
    //       yuzde: response.yuzde || 0,
    //       kupon: response.kupon || 0,
    //       message: response.message || '',
    //       success: response.success || false
    //     };
    //   },
    // }),

    // Sepet paylaşma işlemleri
    shareCart: builder.mutation({
      async queryFn(arg, { getState }, _extraOptions, fetchWithBaseQuery) {
        try {
          // Paylaşım kodu oluştur
          const shareCode = generateRandomCode();
          
          // Mevcut sepeti al
          const cart = getState().cartApi.queries['getCart(undefined)']?.data || [];
          
          // Kupon bilgisini getir
          const couponResult = await fetchWithBaseQuery({
            url: `/cart/coupon?ref=${shareCode}`,
            method: 'GET'
          });
          const cupon = couponResult.data || { yuzde: 0, kupon: 0 };
          
          // Sepet ürünlerini formatla
          const cartItems = cart.map(item => {
            const defaultSize = isRingProduct(item) ? 15 : 0;
            return {
              urun_id: item.id || item.urun_id,
              size: item.size || defaultSize,
              quantity: item.quantity || 1
            };
          });
          
          // Paylaşım isteği gönder
          const result = await fetchWithBaseQuery({
            url: '/cart/share',
            method: 'POST',
            body: {
              share_code: shareCode,
              cart_items: cartItems,
              cupon
            }
          });
          
          if (result.error) return { error: result.error };
          
          if (result.data && result.data.success) {
            return { data: { shareCode, ...result.data } };
          } else {
            return { 
              error: { 
                status: 'CUSTOM_ERROR', 
                data: result.data?.message || 'Sepet paylaşma işlemi başarısız oldu' 
              } 
            };
          }
        } catch (error) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error: error.message || 'Sepet paylaşılırken bir hata oluştu'
            }
          };
        }
      },
      invalidatesTags: ['SharedCart'],
    }),
    
    // Paylaşılan sepeti yükleme
    loadSharedCart: builder.query({
      query: (shareCode) => `/cart/share?share_ref=${shareCode}`,
      transformResponse: (response, meta, arg) => {
        if (Array.isArray(response) && response.length > 0) {
          // Sepet bilgisini dön
          return response;
        }
        throw new Error('Paylaşılan sepette ürün bulunamadı');
      },
      providesTags: ['SharedCart'],
    }),
    
    // Paylaşılan sepeti mevcut sepete ekleme
    addSharedCartToMyCart: builder.mutation({
      async queryFn(selectedSharedItems, _api, _extraOptions, fetchWithBaseQuery) {
        if (!selectedSharedItems || selectedSharedItems.length === 0) {
          return { 
            error: { 
              status: 'CUSTOM_ERROR', 
              data: 'Eklenecek ürün bulunamadı' 
            } 
          };
        }

        try {
          let successCount = 0;
          const errors = [];

          // Her bir ürünü sepete ekle
          for (const item of selectedSharedItems) {
            const result = await fetchWithBaseQuery({
              url: '/cart',
              method: 'POST',
              body: {
                urun_id: item.id,
                ...(item.size !== null && item.size !== undefined && { size: item.size })
              }
            });

            if (result.data && result.data.message) {
              successCount++;
            } else {
              errors.push(result.error || 'Ürün eklenemedi');
            }
          }

          // Sonuçları dön
          if (successCount === selectedSharedItems.length) {
            return {
              data: {
                success: true,
                message: `${successCount} ürün başarıyla sepetinize eklendi.`
              }
            };
          } else if (successCount > 0) {
            return {
              data: {
                success: true,
                message: `${successCount} ürün sepetinize eklendi, ${selectedSharedItems.length - successCount} ürün eklenemedi.`,
                errors
              }
            };
          } else {
            return {
              error: {
                status: 'CUSTOM_ERROR',
                data: 'Ürünler sepetinize eklenemedi.',
                errors
              }
            };
          }
        } catch (error) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error: error.message || 'İşlem sırasında bir hata oluştu.'
            }
          };
        }
      },
      invalidatesTags: ['Cart'],
    }),
  }),
});

export const {
  useGetCartQuery,
  useAddToCartMutation,
  useDeleteFromCartMutation,
  useClearCartMutation,
  useAddServiceMutation,
  useGetServicesQuery,
  useDeleteServiceMutation,
  // useGetCouponByRefQuery,
  useShareCartMutation,
  useLoadSharedCartQuery,
  useAddSharedCartToMyCartMutation,
} = cartApi; 